import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import RecordDialog from "../components/RecordDialog";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { MagnifyingGlass, Circle, Plus, CaretRight, FunnelSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const DOT = { GREEN: "bg-emerald-400", AMBER: "bg-amber-400", RED: "bg-rose-500" };

export default function Tyres() {
  const { meta, currentSite, user } = useAuth();
  const navigate = useNavigate();
  const [tyres, setTyres] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [action, setAction] = useState(null);
  const [status, setStatus] = useState("all");
  const [size, setSize] = useState("all");
  const [q, setQ] = useState("");

  const load = () => {
    const p = new URLSearchParams();
    if (status !== "all") p.set("status", status);
    if (size !== "all") p.set("size", size);
    if (q) p.set("q", q);
    api.get(`/tyres?${p.toString()}`).then(({ data }) => setTyres(data));
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [status, size, q]);

  const clearFilters = () => { setStatus("all"); setSize("all"); setQ(""); };
  const filtered = status !== "all" || size !== "all" || q;

  const isAdmin = user?.role === "admin";
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => s.size === tyres.length ? new Set() : new Set(tyres.map((t) => t.id)));
  const delOne = async (id, serial) => {
    await api.delete(`/tyres/${id}`);
    setSelected((s) => { const n = new Set(s); n.delete(id); return n; });
    load();
    toast.success(`Tyre ${serial} deleted`, { duration: 8000,
      action: { label: "Undo", onClick: async () => { await api.post(`/tyres/${id}/restore`); load(); toast.success(`Tyre ${serial} restored`); } } });
  };
  const delBulk = async () => {
    const ids = [...selected];
    if (!window.confirm(`Delete ${ids.length} selected tyre(s)?`)) return;
    await api.post("/tyres/bulk-delete", { ids });
    setSelected(new Set()); load();
    toast.success(`${ids.length} tyre(s) deleted`, { duration: 8000,
      action: { label: "Undo", onClick: async () => { await api.post("/tyres/bulk-restore", { ids }); load(); toast.success("Tyres restored"); } } });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Tyre Registry</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && selected.size > 0 && (
            <Button variant="outline" onClick={delBulk} data-testid="bulk-delete-tyres"
              className="border-rose-500/40 text-rose-400 hover:bg-rose-500/10"><Trash size={16} weight="bold" className="mr-1" /> Delete ({selected.size})</Button>
          )}
          <Button onClick={() => setAction("fit")} data-testid="quick-fit" className="bg-emerald-500 font-bold text-zinc-950 hover:bg-emerald-400"><Plus size={16} weight="bold" className="mr-1" /> Fit Tyre</Button>
          <Button variant="outline" onClick={() => setAction("remove")} data-testid="quick-remove">Remove</Button>
        </div>
      </div>

      {/* Filters + legend */}
      <div className="flex flex-col gap-3 rounded-sm border border-zinc-800 bg-zinc-900 p-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <FunnelSimple size={16} className="text-zinc-500" />
          <div className="flex items-center gap-2 rounded-sm border border-zinc-800 bg-zinc-950 px-3">
            <MagnifyingGlass size={15} className="text-zinc-500" />
            <Input data-testid="tyre-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search serial number" className="w-48 border-0 bg-transparent px-0 focus-visible:ring-0" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="tyre-status-filter" className="w-36 bg-zinc-950"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent className="max-h-72"><SelectItem value="all">All Status</SelectItem>
              {(meta?.tyre_status || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={size} onValueChange={setSize}>
            <SelectTrigger data-testid="tyre-size-filter" className="w-36 bg-zinc-950"><SelectValue placeholder="Size" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Sizes</SelectItem>
              {(meta?.tyre_sizes || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          {filtered && <Button variant="ghost" size="sm" onClick={clearFilters} className="text-zinc-400 hover:text-amber-500" data-testid="clear-filters">Clear</Button>}
        </div>
        <div className="flex items-center gap-4 text-xs text-zinc-400">
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-400" /> Healthy</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-400" /> Monitor</span>
          <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-rose-500" /> Critical</span>
        </div>
      </div>

      {/* Table */}
      {tyres.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-sm border border-dashed border-zinc-800 py-16 text-center">
          <Circle size={40} className="text-zinc-700" />
          <div className="font-display text-lg font-bold">{filtered ? "No tyres match your filters" : "No tyres registered yet"}</div>
          <p className="max-w-sm text-sm text-zinc-500">{filtered ? "Try clearing the filters to see all tyres." : "Fit your first tyre to a dumper — it will be registered here automatically."}</p>
          {filtered ? <Button variant="outline" onClick={clearFilters}>Clear filters</Button>
            : <Button onClick={() => setAction("fit")} className="bg-emerald-500 font-bold text-zinc-950 hover:bg-emerald-400"><Plus size={16} weight="bold" className="mr-1" /> Fit a Tyre</Button>}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-sm border border-zinc-800">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="bg-zinc-900/80 text-left overline text-zinc-500">
              <tr>
                <th className="px-3 py-3 w-8">{isAdmin && <Checkbox checked={tyres.length > 0 && selected.size === tyres.length} onCheckedChange={toggleAll} data-testid="select-all-tyres" />}</th>
                <th className="px-4 py-3">Serial</th>
                <th className="px-4 py-3">Size</th>
                <th className="px-4 py-3">Current Location</th>
                <th className="px-4 py-3">Condition</th>
                <th className="px-4 py-3">Problem / Damage</th>
                <th className="px-4 py-3">Tread</th>
                <th className="px-4 py-3 text-right">Engine Hrs</th>
                <th className="px-4 py-3 text-right">Cost / Hr</th>
                <th className="px-4 py-3 text-center">Punctures</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 w-8" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {tyres.map((t) => (
                <tr key={t.id} data-testid={`tyre-row-${t.id}`} onClick={() => navigate(`/tyres/${t.id}`)}
                  className="group cursor-pointer bg-zinc-950 transition-colors hover:bg-zinc-900">
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>{isAdmin && <Checkbox checked={selected.has(t.id)} onCheckedChange={() => toggle(t.id)} data-testid={`select-tyre-${t.id}`} />}</td>
                  <td className="px-4 py-3 font-mono font-bold text-amber-500">{t.serial_number}</td>
                  <td className="px-4 py-3 font-mono text-zinc-300">{t.size}</td>
                  <td className="px-4 py-3">
                    {t.dumper_number ? (
                      <div><div className="font-medium text-zinc-200">{t.dumper_number}</div><div className="text-xs text-zinc-500">{t.current_position}</div></div>
                    ) : <span className="text-xs text-zinc-500">Off vehicle</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium ${t.condition === "Repaired" ? "text-amber-400" : "text-zinc-400"}`}>{t.condition || "—"}</span>
                  </td>
                  <td className="px-4 py-3">
                    {t.last_damage_type
                      ? <span className="inline-block rounded-sm bg-rose-500/10 px-2 py-0.5 text-xs font-medium text-rose-400" data-testid={`damage-${t.id}`}>{t.last_damage_type}</span>
                      : <span className="text-xs text-zinc-600">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-2">
                      <span className={`h-2.5 w-2.5 rounded-full ${DOT[t.metrics?.health] || "bg-zinc-600"}`} data-testid={`tread-${t.id}`} />
                      <span className="tabular-nums text-zinc-300">{t.metrics?.current_tread_depth != null ? `${t.metrics.current_tread_depth} mm` : "—"}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-zinc-300">{Math.round(t.metrics?.total_hours || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-zinc-300">{t.metrics?.cost_per_hour ? `R ${t.metrics.cost_per_hour}` : "—"}</td>
                  <td className="px-4 py-3 text-center tabular-nums">
                    {t.metrics?.puncture_count > 0
                      ? <span className="inline-flex min-w-6 justify-center rounded-full bg-rose-500/15 px-2 py-0.5 text-xs font-semibold text-rose-400">{t.metrics.puncture_count}</span>
                      : <span className="text-zinc-600">0</span>}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-2">
                      {isAdmin && <button onClick={() => delOne(t.id, t.serial_number)} data-testid={`delete-tyre-${t.id}`} className="text-zinc-600 transition-colors hover:text-rose-500"><Trash size={15} /></button>}
                      <CaretRight size={16} className="text-zinc-600 transition-colors group-hover:text-amber-500" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {action && <RecordDialog action={action} open={!!action} siteId={currentSite?.id} onOpenChange={(o) => !o && setAction(null)} onDone={load} />}
    </div>
  );
}
